import React, { useState, useEffect } from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import config from "./global/config";

const FactureDetailsGrid = ({
  factureDetails,
  products,
  loading,
  colors,
  onAddDetail,
  onEditDetail,
  onDeleteDetail,
  factureId,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    id_produit: "",
    quantite: "",
    prix_facture_unitaire: "",
  });

  // const calculateTotalAmount = (details) => {
  //   return details.reduce((total, detail) => {
  //     return total + detail.quantite * detail.prix_facture_unitaire;
  //   }, 0);
  // };
  useEffect(() => {
    if (formData.id_produit) {
      const product = products.find(
        (p) => p.id_produit === formData.id_produit
      );
      setSelectedProduct(product);
    }
  }, [formData.id_produit, products]);

  const calculateTotalAmount = (details) => {
    return details.reduce((total, detail) => {
      return total + detail.quantite * detail.prix_facture_unitaire;
    }, 0);
  };

  const updateFactureTotalAmount = async (newAmount) => {
    try {
      if (!factureId) {
        throw new Error("Facture ID is required");
      }

      const response = await fetch(
        `${config.apiUrl}/factures.php?id_facture=${factureId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch facture data: ${response.status}`);
      }

      const factureData = await response.json();
      if (!factureData || !factureData[0]) {
        throw new Error("Facture data not found");
      }

      const formDataToSend = new FormData();
      formDataToSend.append("METHOD", "PUT");
      formDataToSend.append("id_client", factureData[0].id_client);
      formDataToSend.append("categorie_code", factureData[0].categorie_code);
      formDataToSend.append("numero_facture", factureData[0].numero_facture);
      formDataToSend.append("date_facture", factureData[0].date_facture);
      formDataToSend.append("montant_total", newAmount.toFixed(2));
      formDataToSend.append("type_paiement", factureData[0].type_paiement);
      formDataToSend.append("statut_paiement", factureData[0].statut_paiement);
      formDataToSend.append("commentaire", factureData[0].commentaire || "");
      formDataToSend.append(
        "user_upd",
        localStorage.getItem("username") || "SYSTEM"
      );

      const updateResponse = await fetch(
        `${config.apiUrl}/factures.php?id_facture=${factureId}`,
        {
          method: "POST",
          body: formDataToSend,
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(
            errorJson.error || `Server error: ${updateResponse.status}`
          );
        } catch (e) {
          throw new Error(
            `Server error: ${updateResponse.status} - ${errorText}`
          );
        }
      }

      try {
        const text = await updateResponse.text();
        const data = text ? JSON.parse(text) : {};
        if (data.error) {
          throw new Error(data.error);
        }
        return data;
      } catch (e) {
        if (updateResponse.ok) {
          return { success: true };
        }
        throw e;
      }
    } catch (error) {
      console.error("Error updating facture total amount:", error);
      throw error;
    }
  };
  const handleDelete = async () => {
    try {
      setError(null);
      await onDeleteDetail(selectedDetail.id_detail_facture);

      const updatedDetails = factureDetails.filter(
        (detail) => detail.id_detail_facture !== selectedDetail.id_detail_facture
      );
      const newTotal = calculateTotalAmount(updatedDetails);
      await updateFactureTotalAmount(newTotal);

      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting detail:", error);
      setError(`Failed to delete detail: ${error.message}`);
    }
  };
  const handleAddClick = () => {
    setError(null);
    setFormData({
      id_produit: "",
      quantite: "",
      prix_facture_unitaire: "",
    });
    setSelectedProduct(null);
    setIsAddModalOpen(true);
  };

  const handleEditClick = (detail) => {
    setError(null);
    setSelectedDetail(detail);
    setFormData({
      id_produit: detail.id_produit,
      quantite: detail.quantite,
      prix_facture_unitaire: detail.prix_facture_unitaire,
    });
    const product = products.find((p) => p.id_produit === detail.id_produit);
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (detail) => {
    setError(null);
    setSelectedDetail(detail);
    setIsDeleteModalOpen(true);
  };

  const validateForm = () => {
    if (!formData.id_produit) {
      throw new Error("Please select a product");
    }
    if (!formData.quantite || formData.quantite <= 0) {
      throw new Error("Please enter a valid quantity");
    }
    if (!formData.prix_facture_unitaire || formData.prix_facture_unitaire <= 0) {
      throw new Error("Please enter a valid unit price");
    }

    // Stock validation
    if (!selectedProduct) {
      throw new Error("Product not found");
    }

    const currentStock = selectedProduct.stock_actuel;
    const requestedQuantity = parseInt(formData.quantite);

    // For edit, we need to consider the current quantity
    if (isEditModalOpen && selectedDetail) {
      const additionalQuantity = requestedQuantity - selectedDetail.quantite;
      if (additionalQuantity > currentStock) {
        throw new Error(
          `Insufficient stock. Available: ${currentStock}, Additional requested: ${additionalQuantity}`
        );
      }
    }
    // For new items, just check against the full quantity
    else if (requestedQuantity > currentStock) {
      throw new Error(
        `Insufficient stock. Available: ${currentStock}, Requested: ${requestedQuantity}`
      );
    }
  };

  const handleModalSubmit = async (type) => {
    try {
      setError(null);
      validateForm();

      const detailData = {
        ...formData,
        id_facture: factureId,
        user_cre: localStorage.getItem("username") || "SYSTEM",
      };

      if (type === "add") {
        await onAddDetail(detailData);
      } else if (type === "edit") {
        await onEditDetail(selectedDetail.id_detail_facture, detailData);
      }

      const updatedDetails =
        type === "add"
          ? [
              ...factureDetails,
              {
                ...detailData,
                montant_total:
                  detailData.quantite * detailData.prix_facture_unitaire,
              },
            ]
          : factureDetails.map((detail) =>
              detail.id_detail_facture === selectedDetail?.id_detail_facture
                ? {
                    ...detail,
                    ...detailData,
                    montant_total:
                      detailData.quantite * detailData.prix_facture_unitaire,
                  }
                : detail
            );

      const newTotal = calculateTotalAmount(updatedDetails);

      try {
        await updateFactureTotalAmount(newTotal);
      } catch (totalUpdateError) {
        console.error(
          "Failed to update total, but detail was saved:",
          totalUpdateError
        );
        setError(
          "Detail saved but failed to update total amount. Please refresh the page."
        );
      }

      if (type === "add") {
        setIsAddModalOpen(false);
      } else {
        setIsEditModalOpen(false);
      }

      setFormData({
        id_produit: "",
        quantite: "",
        prix_facture_unitaire: "",
      });
      setSelectedProduct(null);
    } catch (error) {
      console.error("Error handling detail:", error);
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const renderStockInfo = () => {
    if (!selectedProduct) return null;

    const stockColor =
      selectedProduct.stock_actuel > 0 ? "success.main" : "error.main";
    return (
      <Typography color={stockColor} variant="body2" sx={{ mt: 1 }}>
        Stock disponible: {selectedProduct.stock_actuel}
      </Typography>
    );
  };

  const detailsColumns = [
    {
      field: "designation",
      headerName: "Produit",
      flex: 1,
    },
    {
      field: "quantite",
      headerName: "Quantité",
      flex: 1,
      type: "number",
    },
    {
      field: "prix_facture_unitaire",
      headerName: "Prix Unitaire",
      flex: 1,
      type: "number",
    },
    {
      field: "montant_total",
      headerName: "Montant Total",
      flex: 1,
      type: "number",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleEditClick(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteClick(params.row)}
        />,
      ],
    },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Ajouter un détail
        </Button>
      </Box>

      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors?.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors?.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={factureDetails || []}
          columns={detailsColumns}
          getRowId={(row) => row.id_detail_facture}
          autoHeight
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          disableSelectionOnClick
        />
      </Box>

      {/* Add Detail Modal */}
      <Dialog open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <DialogTitle>Ajouter un détail de facture</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            name="id_produit"
            label="Produit"
            value={formData.id_produit}
            onChange={handleInputChange}
            error={!formData.id_produit}
          >
            {Array.isArray(products) && products.map((product) => (
              <MenuItem key={product.id_produit} value={product.id_produit}>
                {product.designation}
              </MenuItem>
            ))}
          </TextField>
          {renderStockInfo()}
          <TextField
            fullWidth
            margin="dense"
            name="quantite"
            label="Quantité"
            type="number"
            value={formData.quantite}
            onChange={handleInputChange}
            error={
              !formData.quantite ||
              formData.quantite <= 0 ||
              (selectedProduct &&
                formData.quantite > selectedProduct.stock_actuel)
            }
            helperText={
              selectedProduct &&
              formData.quantite > selectedProduct.stock_actuel
                ? "Quantité dépasse le stock disponible"
                : ""
            }
          />
          <TextField
            fullWidth
            margin="dense"
            name="prix_facture_unitaire"
            label="Prix unitaire"
            type="number"
            value={formData.prix_facture_unitaire}
            onChange={handleInputChange}
            error={
              !formData.prix_facture_unitaire || formData.prix_facture_unitaire <= 0
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddModalOpen(false)}>Annuler</Button>
          <Button
            onClick={() => handleModalSubmit("add")}
            variant="contained"
            color="secondary"
            disabled={
              !selectedProduct ||
              formData.quantite > selectedProduct.stock_actuel
            }
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Detail Modal */}
      <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <DialogTitle>Modifier le détail de facture</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            name="id_produit"
            label="Produit"
            value={formData.id_produit}
            onChange={handleInputChange}
            error={!formData.id_produit}
          >
            {Array.isArray(products) && products.map((product) => (
              <MenuItem key={product.id_produit} value={product.id_produit}>
                {product.designation}
              </MenuItem>
            ))}
          </TextField>
          {renderStockInfo()}
          <TextField
            fullWidth
            margin="dense"
            name="quantite"
            label="Quantité"
            type="number"
            value={formData.quantite}
            onChange={handleInputChange}
            error={
              !formData.quantite ||
              formData.quantite <= 0 ||
              (selectedProduct &&
                formData.quantite - (selectedDetail?.quantite || 0) >
                  selectedProduct.stock_actuel)
            }
            helperText={
              selectedProduct &&
              formData.quantite - (selectedDetail?.quantite || 0) >
                selectedProduct.stock_actuel
                ? "Quantité dépasse le stock disponible"
                : ""
            }
          />
          <TextField
            fullWidth
            margin="dense"
            name="prix_facture_unitaire"
            label="Prix unitaire"
            type="number"
            value={formData.prix_facture_unitaire}
            onChange={handleInputChange}
            error={
              !formData.prix_facture_unitaire || formData.prix_facture_unitaire <= 0
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditModalOpen(false)}>Annuler</Button>
          <Button
            onClick={() => handleModalSubmit("edit")}
            variant="contained"
            color="secondary"
            disabled={
              !selectedProduct ||
              formData.quantite - (selectedDetail?.quantite || 0) >
                selectedProduct.stock_actuel
            }
          >
            Modifier
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer ce détail de facture ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteModalOpen(false)}>Annuler</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FactureDetailsGrid;
